import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CourseEnroll from "@/modules/courses/containers/CourseEnroll";
import { getActiveStories, isVideoSlide } from "../stories.data";
import StoriesBarView from "../views/StoriesBarView";
import StoryViewerView from "../views/StoryViewerView";

const SLIDE_DURATION_MS = 7000;
// Видео перелистывает слайд само (onEnded); таймер — лишь страховка на случай,
// если автоплей не стартовал. Больше самого длинного ролика.
const VIDEO_FALLBACK_MS = 30000;
const SEEN_STORAGE_KEY = "amani.stories.seen";

const readSeen = (): string[] => {
  try {
    const raw = localStorage.getItem(SEEN_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === "string") : [];
  } catch {
    return [];
  }
};

export default function Stories() {
  const stories = useMemo(() => getActiveStories(), []);
  const [seenIds, setSeenIds] = useState<string[]>(readSeen);
  const [position, setPosition] = useState<{ story: number; slide: number } | null>(null);
  const [paused, setPaused] = useState(false);
  const remainingRef = useRef(SLIDE_DURATION_MS);

  const [enrollTypeRecId, setEnrollTypeRecId] = useState<string | null>(null);

  const open = useCallback((index: number) => setPosition({ story: index, slide: 0 }), []);
  const close = useCallback(() => setPosition(null), []);

  // «Записаться» из сторис: закрываем просмотрщик и открываем оформление — так не
  // воюем со стеком модалок и таймером слайдов.
  const openEnroll = useCallback((typeRecId: string) => {
    setPosition(null);
    setEnrollTypeRecId(typeRecId);
  }, []);

  // открытая сторис считается просмотренной; здесь, а не в обработчиках, —
  // чтобы покрыть и открытие с ленты, и автопереход на следующую сторис
  const openStoryId = position ? stories[position.story].id : null;

  useEffect(() => {
    if (!openStoryId) return;
    setSeenIds((prev) => {
      if (prev.includes(openStoryId)) return prev;
      const next = [...prev, openStoryId];
      try {
        localStorage.setItem(SEEN_STORAGE_KEY, JSON.stringify(next));
      } catch {
        // приватный режим / переполненное хранилище — не критично
      }
      return next;
    });
  }, [openStoryId]);

  const next = useCallback(() => {
    setPosition((prev) => {
      if (!prev) return prev;
      if (prev.slide + 1 < stories[prev.story].slides.length) return { ...prev, slide: prev.slide + 1 };
      const nextStory = prev.story + 1;
      return nextStory >= stories.length ? null : { story: nextStory, slide: 0 };
    });
  }, [stories]);

  const prev = useCallback(() => {
    setPosition((current) => {
      if (!current) return current;
      if (current.slide > 0) return { ...current, slide: current.slide - 1 };
      const prevStory = current.story - 1;
      if (prevStory < 0) return current;
      return { story: prevStory, slide: stories[prevStory].slides.length - 1 };
    });
  }, [stories]);

  const activeSlide = position ? stories[position.story].slides[position.slide] : null;
  const activeDurationMs =
    activeSlide && isVideoSlide(activeSlide) ? VIDEO_FALLBACK_MS : SLIDE_DURATION_MS;

  // новый слайд — полный отсчёт заново
  useEffect(() => {
    remainingRef.current = activeDurationMs;
  }, [position?.story, position?.slide, activeDurationMs]);

  // на паузе таймер замирает на остатке — синхронно с CSS-анимацией полоски
  useEffect(() => {
    if (!position || paused) return;
    const startedAt = Date.now();
    const timer = setTimeout(next, remainingRef.current);
    return () => {
      clearTimeout(timer);
      remainingRef.current = Math.max(0, remainingRef.current - (Date.now() - startedAt));
    };
  }, [position, paused, next]);

  // предзагружаем следующий слайд, чтобы переключение не мигало
  useEffect(() => {
    if (!position) return;
    const slides = stories[position.story].slides;
    const upcoming = slides[position.slide + 1] ?? stories[position.story + 1]?.slides[0];
    if (upcoming && !isVideoSlide(upcoming)) new Image().src = upcoming;
  }, [position, stories]);

  if (stories.length === 0) return null;

  return (
    <>
      <StoriesBarView stories={stories} seenIds={seenIds} onOpen={open} />

      {position && (
        <StoryViewerView
          story={stories[position.story]}
          slideIndex={position.slide}
          slideDurationMs={SLIDE_DURATION_MS}
          paused={paused}
          onPrev={prev}
          onNext={next}
          onClose={close}
          onEnroll={openEnroll}
          onPauseChange={setPaused}
        />
      )}

      {enrollTypeRecId && (
        <CourseEnroll typeRecId={enrollTypeRecId} onClose={() => setEnrollTypeRecId(null)} />
      )}
    </>
  );
}
